import { Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { CompetitionPeer } from '../competitor-analysis/competitor-analysis';
import { FundamentalScoreSystemKey, scoreColorVar } from '../services/api/fundamental-scores';
import { competitorUpsidePercent, consensusLabel } from '../analysts-data';
import { ForexService } from '../services/forex';
import { toV3ColorVar } from '../v3-scores-overview/score-color';
import { V3TabPanel } from '../v3-tab-panel/v3-tab-panel';

type ScoreKey = FundamentalScoreSystemKey;
type GroupKey = 'analysts' | 'earnings' | 'scores';

interface ScoreCell {
  key: ScoreKey;
  label: string;
  display: string;
  shortDisplay: string;
  colorVar: string;
}

interface CompetitorRow {
  name: string;
  isin: string;
  zeroLinkIsin: string | null;
  icon: string | null;
  isSubject: boolean;
  rating: string | null;
  ratingColor: 'positive' | 'negative' | 'neutral';
  potential: string | null;
  potentialPositive: boolean;
  epsLabel: string | null;
  surprise: string | null;
  surprisePositive: boolean;
  scores: ScoreCell[];
}

interface Metric {
  key: string;
  label: string;
  value: string;
  colorVar: string;
}

const SCORE_LABEL: Record<ScoreKey, string> = {
  quality: 'Qualität',
  growth: 'Wachstum',
  dividend: 'Dividende',
  the_big_call: 'Robustheit',
};
// TraderFox always scores on a 0-15 scale, matching every other TraderFox surface in the app.
const SCORE_MAX = 15;
const SCORE_ORDER: ScoreKey[] = ['quality', 'growth', 'dividend', 'the_big_call'];

const GROUP_TABS: { key: GroupKey; label: string }[] = [
  { key: 'analysts', label: 'Analysten' },
  { key: 'earnings', label: 'Earnings' },
  { key: 'scores', label: 'Scores' },
];

// Every missing-value "–" uses the per-theme default text color, score columns included.
const MISSING_VALUE_COLOR_VAR = '--color-white';

// V3's "competitors tab" — the card-list restyle of V2's CompetitorsTable. See CLAUDE.md.
@Component({
  selector: 'app-v3-competitor-table',
  imports: [V3TabPanel],
  standalone: true,
  templateUrl: './v3-competitor-table.html',
  styleUrl: './v3-competitor-table.scss',
})
export class V3CompetitorTable {
  private readonly forexService = inject(ForexService);

  @Input() peers: CompetitionPeer[] = [];
  @Input() limit = 4;
  @Input() desktop = false;
  @Input() finnetMobile = false;
  /** The analyzed stock itself, highlighted as the first row. Desktop only (Zero +
   * finnetWeb) — V3CompetitorOverview's card never sets it. */
  @Input() subject: CompetitionPeer | null = null;
  /** Live EUR price per ISIN, for the Potenzial recompute. */
  @Input() livePrices: Record<string, number> | null = null;
  @Output() navigateToInstrument = new EventEmitter<string>();

  readonly groupTabs = GROUP_TABS;
  readonly activeGroup = signal<GroupKey>('analysts');

  // Plain getter, not computed() — peers/limit are plain @Input()s, and computed() only
  // tracks signal reads, so it would freeze at whatever peers was on first read (same
  // pitfall documented on V3FinnetContentGrid's leftColumn/rightColumn).
  get rows(): CompetitorRow[] {
    const peerRows = this.peers.slice(0, this.limit).map((peer) => this.mapPeer(peer, false));
    if (!this.subject) return peerRows;
    return [this.mapPeer(this.subject, true), ...peerRows];
  }

  selectGroup(key: GroupKey) {
    this.activeGroup.set(key);
  }

  isGroupActive(key: GroupKey): boolean {
    return this.activeGroup() === key;
  }

  onRowClick(zeroLinkIsin: string | null) {
    if (zeroLinkIsin) this.navigateToInstrument.emit(zeroLinkIsin);
  }

  metricsFor(row: CompetitorRow): Metric[] {
    switch (this.activeGroup()) {
      case 'earnings':
        return this.earningsMetrics(row);
      case 'scores':
        return this.scoresMetrics(row);
      default:
        return this.analystsMetrics(row);
    }
  }

  private analystsMetrics(row: CompetitorRow): Metric[] {
    return [
      {
        key: 'potential',
        label: 'Potenzial',
        value: row.potential ?? '–',
        colorVar: this.potentialColorVar(row),
      },
      {
        key: 'rating',
        label: 'Rating',
        value: row.rating ?? '–',
        colorVar: this.ratingColorVar(row),
      },
    ];
  }

  private earningsMetrics(row: CompetitorRow): Metric[] {
    return [
      {
        key: 'eps',
        label: 'EPS',
        value: row.epsLabel ?? '–',
        colorVar: this.epsLabelColorVar(row),
      },
      {
        key: 'surprise',
        label: 'Überraschung',
        value: row.surprise ?? '–',
        colorVar: this.surpriseColorVar(row),
      },
    ];
  }

  private scoresMetrics(row: CompetitorRow): Metric[] {
    return row.scores.map((score) => ({
      key: score.key,
      label: score.label,
      value: score.display,
      colorVar: score.colorVar,
    }));
  }

  // Same content as the mobile card list, except Rating has no sign coloring — always the
  // default text color, unlike Potenzial (still colored by sign).
  finnetMobileMetricsFor(row: CompetitorRow): Metric[] {
    if (this.activeGroup() !== 'analysts') return this.metricsFor(row);
    return [
      {
        key: 'potential',
        label: 'Potenzial',
        value: row.potential ?? '–',
        colorVar: this.potentialColorVar(row),
      },
      {
        key: 'rating',
        label: 'Rating',
        value: row.rating ?? '–',
        colorVar: row.rating ? '--color-white' : MISSING_VALUE_COLOR_VAR,
      },
    ];
  }

  // Header labels for the active group — derived from the same metrics every row already
  // renders, so the header can never drift out of sync with the row cells below it. Keeps
  // each metric's key too, so the header cell can get the same per-column width as the rows.
  headerMetrics(): Metric[] {
    const first = this.rows[0];
    return first ? this.finnetMobileMetricsFor(first) : [];
  }

  ratingColorVar(row: CompetitorRow): string {
    if (!row.rating) return MISSING_VALUE_COLOR_VAR;
    if (row.ratingColor === 'positive') return '--v3-positive';
    if (row.ratingColor === 'negative') return '--v3-negative';
    return '--v3-muted';
  }

  potentialColorVar(row: CompetitorRow): string {
    if (!row.potential) return MISSING_VALUE_COLOR_VAR;
    return row.potentialPositive ? '--v3-positive' : '--v3-negative';
  }

  epsLabelColorVar(row: CompetitorRow): string {
    if (row.epsLabel === 'Beat') return '--v3-positive';
    if (row.epsLabel === 'Miss') return '--v3-negative';
    return MISSING_VALUE_COLOR_VAR;
  }

  surpriseColorVar(row: CompetitorRow): string {
    if (!row.surprise) return MISSING_VALUE_COLOR_VAR;
    return row.surprisePositive ? '--v3-positive' : '--v3-negative';
  }

  private mapPeer(peer: CompetitionPeer, isSubject: boolean): CompetitorRow {
    const { analyst, eps, traderfox } = peer;

    const ratingRaw = analyst.consensus_label ?? analyst.consensus;
    const rating = ratingRaw ? consensusLabel(ratingRaw) : null;
    let ratingColor: 'positive' | 'negative' | 'neutral' = 'neutral';
    if (analyst.sentiment === 'positive') ratingColor = 'positive';
    else if (analyst.sentiment === 'negative') ratingColor = 'negative';

    const potentialPercent = competitorUpsidePercent(
      this.livePrices?.[peer.isin] ?? null,
      analyst.average_price_target,
      analyst.average_price_target_currency,
      analyst.price_target_upside_percent,
      this.forexService,
    );
    const potential = potentialPercent !== null ? this.formatPercent(potentialPercent) : null;

    let epsLabel: string | null = null;
    if (eps.sentiment === 'beat') epsLabel = 'Beat';
    else if (eps.sentiment === 'miss') epsLabel = 'Miss';

    const surprise =
      eps.surprise_percent !== null ? this.formatPercent(eps.surprise_percent) : null;

    return {
      name: peer.name,
      isin: peer.isin,
      zeroLinkIsin: peer.zero_link_isin ?? null,
      icon: peer.logo_url,
      isSubject,
      rating,
      ratingColor,
      potential,
      potentialPositive: (potentialPercent ?? 0) >= 0,
      epsLabel,
      surprise,
      surprisePositive: (eps.surprise_percent ?? 0) >= 0,
      scores: SCORE_ORDER.map((key) =>
        this.formatScore(key, traderfox?.[key] ?? null, traderfox?.warnings?.[key]),
      ),
    };
  }

  // A non-empty warnings entry means the score was withheld — render "?/15" (grey).
  private formatScore(key: ScoreKey, value: number | null, warnings?: unknown): ScoreCell {
    if (Array.isArray(warnings) && warnings.length > 0) {
      return {
        key,
        label: SCORE_LABEL[key],
        display: `?/${SCORE_MAX}`,
        shortDisplay: '?',
        colorVar: toV3ColorVar('--fundamental-score-grey'),
      };
    }
    if (value == null) {
      return {
        key,
        label: SCORE_LABEL[key],
        display: '–',
        shortDisplay: '–',
        colorVar: MISSING_VALUE_COLOR_VAR,
      };
    }
    return {
      key,
      label: SCORE_LABEL[key],
      display: `${Math.round(value)}/${SCORE_MAX}`,
      shortDisplay: `${Math.round(value)}`,
      colorVar: toV3ColorVar(scoreColorVar(value, SCORE_MAX)),
    };
  }

  private formatPercent(value: number): string {
    const abs = Math.abs(value).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return value >= 0 ? `+${abs}%` : `-${abs}%`;
  }
}
